/**
 * 云端编程里 AskUserQuestion 的问题选择器。
 *
 * 沙箱 agent-runner 会把 AskUserQuestion 转发到 host(其余工具自动放行),
 * 它不是「允许/拒绝」式的权限,而是 agent 在向用户提问。这里把问题与选项
 * 渲染成可点选的卡片,提交后通过 updatedInput.answers 回传给 agent,
 * 与 cowork 端 CoworkInlinePermission 的契约保持一致(多选用 '|||' 连接)。
 */
import React from 'react';
import FollowupQuestionForm from '../components/common/FollowupQuestionForm';
import { i18nService } from '../services/i18n';
import type { CodingPermissionRequest } from './types';

type QuestionOption = { label: string; description?: string };
type QuestionItem = {
  question: string;
  header?: string;
  options: QuestionOption[];
  multiSelect?: boolean;
};

interface Props {
  permission: CodingPermissionRequest;
  onSubmit: (updatedInput: Record<string, unknown>) => void;
  onDeny: () => void;
}

/** 从 toolInput 解析出标准化的问题列表(兼容缺字段/异常项)。 */
export function parseAskUserQuestions(toolInput: unknown): QuestionItem[] {
  if (!toolInput || typeof toolInput !== 'object') return [];
  const rawQuestions = (toolInput as Record<string, unknown>).questions;
  if (!Array.isArray(rawQuestions)) return [];
  return rawQuestions
    .map((question) => {
      if (!question || typeof question !== 'object') return null;
      const record = question as Record<string, unknown>;
      const options = Array.isArray(record.options)
        ? (record.options
            .map((option) => {
              if (!option || typeof option !== 'object') return null;
              const optionRecord = option as Record<string, unknown>;
              if (typeof optionRecord.label !== 'string') return null;
              return {
                label: optionRecord.label,
                description:
                  typeof optionRecord.description === 'string' ? optionRecord.description : undefined,
              } as QuestionOption;
            })
            .filter(Boolean) as QuestionOption[])
        : [];
      if (typeof record.question !== 'string' || options.length === 0) return null;
      return {
        question: record.question,
        header: typeof record.header === 'string' ? record.header : undefined,
        options,
        multiSelect: Boolean(record.multiSelect),
      } as QuestionItem;
    })
    .filter(Boolean) as QuestionItem[];
}

/** 云端编程复用追问表单，仍由调用方通过原权限接口提交。 */
const CodingQuestionPrompt: React.FC<Props> = ({ permission, onSubmit, onDeny }) => (
  <FollowupQuestionForm key={permission.requestId} questions={parseAskUserQuestions(permission.toolInput)} language={i18nService.getLanguage()}
    onSubmit={(answers) => onSubmit({ ...(permission.toolInput as Record<string, unknown>), answers })}
    onDeny={onDeny} />
);

export default CodingQuestionPrompt;
